import type {ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {BrandKitSwatchCluster} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {noop} from '@Utils/general.util';
import {useBrandKitWithAssets} from '@Hooks/brands/useBrandKitWithAssets';
import {useLoadBrandKitAssets} from '@Hooks/brands/useLoadBrandKitAssets';
import {getOrderedBrandColorHexes} from '@Libraries/brand-kit-display.library';
import {BRAND_KIT_SWATCH_COLORS_LIMIT, BrandKitSwatchClusterVariant} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster.types';
import {canUserAccessBrandKits, doesBrandHaveAnyAssetType, openAppropriateUpsellingDialogForBrandKits} from '@Libraries/brands-library';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/esm/Overlay';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import type {AiPromptBoxBrandKitSelectorProps} from './ai-prompt-box-brand-kit-selector.types';
import {BRAND_KIT_SELECTOR_MODAL_PANEL_ID} from './ai-prompt-box-brand-kit-selector.types';
import {BrandKitPopup} from './components/brand-kit-popup/brand-kit-popup';
import styles from '../ai-prompt-box-footer-selector.module.scss';
import {getFooterSelectorIconTriggerSizing} from '../ai-prompt-box-footer-selector.library';
import brandKitSelectorStyles from './ai-prompt-box-brand-kit-selector.module.scss';

export function AiPromptBoxBrandKitSelector({promptBoxId}: AiPromptBoxBrandKitSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const {state, actions} = useAiPromptBox(promptBoxId);
  const closeSmallScreenModal = useDefaultPanelClose(BRAND_KIT_SELECTOR_MODAL_PANEL_ID);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);

  const pinnedBrandKitId = state.pinnedBrandKitId ?? null;

  useLoadBrandKitAssets(isOpen || pinnedBrandKitId !== null);

  const iconTriggerSizing = getFooterSelectorIconTriggerSizing(state.size);

  const pinnedBrandKitWithAssets = useBrandKitWithAssets(pinnedBrandKitId ?? '');
  const pinnedBrandKit = pinnedBrandKitWithAssets && doesBrandHaveAnyAssetType(pinnedBrandKitWithAssets.brandKit.brandId) ? pinnedBrandKitWithAssets : null;

  const handleOpen = (): void => {
    setIsTooltipVisible(false);
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsTooltipVisible(false);
    setIsOpen(false);
  };

  const handleBrandKitSelect = (brandKitId: string | null): void => {
    if (brandKitId !== null) {
      trackGA4CustomEvent(GA4EventName.AICHAT_BRANDKIT_SELECTED);
    } else if (pinnedBrandKitId !== null) {
      trackGA4CustomEvent(GA4EventName.AICHAT_BRANDKIT_REMOVED);
    }

    actions.update({pinnedBrandKitId: brandKitId});
    handleClose();
    closeSmallScreenModal();
  };

  const getPopupContent = (isFullWidth = false): ReactElement => {
    return <BrandKitPopup pinnedBrandKitId={pinnedBrandKitId} onBrandKitSelect={handleBrandKitSelect} isFullWidth={isFullWidth} />;
  };

  const pinnedColorHexes = pinnedBrandKit ? getOrderedBrandColorHexes(pinnedBrandKit.colors, BRAND_KIT_SWATCH_COLORS_LIMIT) : [];

  const hasPinnedColors = pinnedColorHexes.length > 0;

  const getPinnedTriggerContent = (): ReactElement => {
    if (hasPinnedColors) {
      return <BrandKitSwatchCluster colorHexes={pinnedColorHexes} variant={BrandKitSwatchClusterVariant.COMPACT} />;
    }

    return <Icon icon="icon-color-palette" size={IconSize.SIZE_ICON_16} />;
  };

  const getTrigger = (onTriggerClick: () => void): ReactElement => {
    if (!pinnedBrandKit) {
      return (
        <ClickableDiv onClick={onTriggerClick} className={`${styles.iconTrigger} ${iconTriggerSizing.triggerClass}`} aria-label={window.i18next.t('pmwjs_brand_kit')}>
          <Icon icon="icon-color-palette" size={iconTriggerSizing.iconSize} />
        </ClickableDiv>
      );
    }

    return (
      <ClickableDiv onClick={onTriggerClick} className={styles.pinnedTrigger} aria-label={window.i18next.t('pmwjs_brand_kit')}>
        {getPinnedTriggerContent()}
        <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} />
      </ClickableDiv>
    );
  };

  const openBrandKitsUpsellDialog = (): void => {
    setIsTooltipVisible(false);
    openAppropriateUpsellingDialogForBrandKits();
  };

  const getTooltip = (): OverlayChildren => {
    return (
      <Tooltip className="spacing-m-b-2 _rounded-arrow">
        <Text val={window.i18next.t('pmwjs_brand_kit')} size={TextSize.MD} />
      </Tooltip>
    );
  };

  const getDropdown = (): ReactElement => {
    return (
      <Dropdown
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        isResumable
        doCloseOnInnerClick={false}
        showModalForSmallScreen
        isSmallScreenModalBottomSheet
        modalTitle={window.i18next.t('pmwjs_brand_kits')}
        modalShowBackButton
        modalShowCloseButton={false}
        modalDisableDragToClose
        panelIdForModal={BRAND_KIT_SELECTOR_MODAL_PANEL_ID}
        modalClassName="spacing-p-l-4 spacing-p-r-4 spacing-p-b-4"
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        position={DROPDOWN_POSITION.FIXED}
        popUpHasCustomWidth
        popupClassName={`${styles.popup} ${brandKitSelectorStyles.clippedPopup}`}
        popupSpacingClass="spacing-p-l-3 spacing-p-r-3 spacing-p-b-3"
        selector={getTrigger(noop)}
        selectorClassNameOnOpen={pinnedBrandKit ? styles.openedPinnedTrigger : styles.openedIconTrigger}
        popup={getPopupContent()}
        modalContent={getPopupContent(true)}
        popupOffset={8}
      />
    );
  };

  /** Without the feature, the trigger sells it instead of opening the picker — matching every other brand-kit surface. */
  const getSelectorContent = (): ReactElement => {
    if (!canUserAccessBrandKits()) {
      return getTrigger(openBrandKitsUpsellDialog);
    }

    return getDropdown();
  };

  return (
    <OverlayTrigger
      show={isTooltipVisible && !isOpen && !isMobile}
      onToggle={setIsTooltipVisible}
      overlay={getTooltip()}
      delay={{show: 200, hide: 200}}
      placement="top"
      trigger={['hover', 'focus']}
    >
      <div className={styles.overlayTriggerWrapper}>{getSelectorContent()}</div>
    </OverlayTrigger>
  );
}
